/**
 * المراقبة.
 * شروط التنبيه flex:1 1 240px · مساحة لمس 44px · Empty State بإطار متقطّع.
 */

import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Button, Card, SectionTitle } from '@/components/primitives/Primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { useWatchlist } from '@/services/market';
import { useToast } from '@/app/AppShell';
import { num, pct } from '@/lib/format';
import type { WatchAlertConditions, WatchItem } from '@/domain/types';

const CONDITION_LABELS: { key: keyof WatchAlertConditions; label: string }[] = [
  { key: 'priceDrop', label: 'انخفاض السعر' },
  { key: 'newDeals', label: 'صفقات جديدة' },
  { key: 'supplyChange', label: 'تغيّر المعروض' },
  { key: 'opportunityJump', label: 'قفزة في مؤشر الفرصة' },
];

function WatchCard({
  item,
  onOpen,
  onRemove,
  onToggle,
}: {
  item: WatchItem;
  onOpen: () => void;
  onRemove: () => void;
  onToggle: (key: keyof WatchAlertConditions) => void;
}): JSX.Element {
  return (
    <Card>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{item.district}</div>
          <div style={{ fontSize: 12, color: color.textSubtle, marginTop: 4 }}>
            {item.city} · أُضيف <span className="nq-num" dir="ltr">{item.addedAt}</span>
          </div>
        </div>
        <div style={{ flex: '0 1 auto', display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          <div>
            <div className="nq-label">سعر التنفيذ</div>
            <div className="nq-num" dir="ltr" style={{ fontSize: 19, fontWeight: 600, marginTop: 4 }}>
              {num(item.dealPricePerSqm)}
            </div>
          </div>
          <div>
            <div className="nq-label">التغيّر</div>
            <div
              className="nq-num"
              dir="ltr"
              style={{
                fontSize: 19,
                fontWeight: 600,
                marginTop: 4,
                color: item.changePct >= 0 ? color.green : color.red,
              }}
            >
              {pct(item.changePct)}
            </div>
          </div>
          <div>
            <div className="nq-label">مؤشر الفرصة</div>
            <div className="nq-num" dir="ltr" style={{ fontSize: 19, fontWeight: 600, marginTop: 4, color: color.teal }}>
              {item.opportunityScore}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="nq-label" style={{ marginBottom: 10 }}>شروط التنبيه</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {CONDITION_LABELS.map((c) => (
            <label
              key={c.key}
              style={{
                flex: '1 1 240px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 44,
                padding: '8px 12px',
                borderRadius: 12,
                background: color.surface700,
                border: `1px solid ${color.hairlineSoft}`,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={item.conditions[c.key]}
                onChange={() => onToggle(c.key)}
                style={{ accentColor: color.teal, width: 16, height: 16 }}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
        <Button onClick={onOpen}>افتح الحي</Button>
        <Button variant="ghost" onClick={onRemove}>
          إزالة
        </Button>
      </div>
    </Card>
  );
}

export default function WatchlistRoute(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { state, remove, updateConditions } = useWatchlist();

  return (
    <div className="nq-enter" style={{ paddingTop: 36, display: 'grid', gap: 18 }}>
      <SectionTitle title="المراقبة" />

      {state.status === 'loading' ? (
        <LoadingState message="نحمّل قائمة المراقبة..." blocks={2} />
      ) : state.status === 'error' ? (
        <ErrorState message="تعذّر تحميل قائمة المراقبة." onRetry={state.reload} />
      ) : state.status === 'empty' ? (
        <EmptyState
          title="لا توجد عناصر تحت المراقبة"
          description="أضف حياً من صفحة الفرص أو من نتيجة التقييم لتصلك تنبيهات عند تغيّر شروطه."
          actionLabel="اكتشف فرصاً للمراقبة"
          onAction={() => navigate('/opportunities')}
        />
      ) : (
        (state.data ?? []).map((item) => (
          <WatchCard
            key={item.id}
            item={item}
            onOpen={() => navigate(`/district/${encodeURIComponent(item.districtId)}`)}
            onRemove={() => {
              void remove(item.districtId);
              toast('أزلنا الحي من المراقبة');
            }}
            onToggle={(key) => {
              void updateConditions(item.districtId, {
                ...item.conditions,
                [key]: !item.conditions[key],
              });
            }}
          />
        ))
      )}
    </div>
  );
}
